import { Injectable, inject } from '@angular/core';
import { Firestore, collection, writeBatch, doc, query, where, getDocs, setDoc, deleteDoc, addDoc, updateDoc, getDoc, disableNetwork, enableNetwork, WriteBatch, limit } from '@angular/fire/firestore';
import * as XLSX from 'xlsx';
import { BehaviorSubject, Observable, Subject, firstValueFrom } from 'rxjs';

// Define the interface matching your fixed Excel format
export interface ReportData {
  id?: string;
  transactionDate: string;
  machineAccount: string;
  storeID: string;
  storeName: string;
  countryType: string;
  operationType: string;
  serverFeeType: string;
  SalesAmount: number;
  IP: string;
  [key: string]: any; // Allow other columns
}

@Injectable({
  providedIn: 'root'
})
export class ExcelImportService {
  private firestore: Firestore = inject(Firestore);
  private _isImporting = new BehaviorSubject<boolean>(false);
  private _importProgress = new BehaviorSubject<number>(0); // 0-100%
  private _currentImportPeriod = new BehaviorSubject<string | null>(null);
  private _isCancelled = false;
  private _cancelSubject = new Subject<void>();

  get isImporting$(): Observable<boolean> {
    return this._isImporting.asObservable();
  }

  get importProgress$(): Observable<number> {
    return this._importProgress.asObservable();
  }

  get currentImportPeriod$(): Observable<string | null> {
    return this._currentImportPeriod.asObservable();
  }

  get cancelSignal$(): Observable<void> {
    return this._cancelSubject.asObservable();
  }

  async cancelImport() {
    this._isCancelled = true;
    this._cancelSubject.next();
    this._isImporting.next(false);
    this._importProgress.next(0);
    this._currentImportPeriod.next(null);

    // Force abort pending network operations to stop backoff errors
    try {
      await disableNetwork(this.firestore);
      setTimeout(() => enableNetwork(this.firestore), 500);
    } catch (e) {
      console.warn('Failed to reset network on cancel', e);
    }
  }

  isCancelled(): boolean {
    return this._isCancelled;
  }

  resetCancellation() {
    this._isCancelled = false;
  }

  async importExcelFile(file: File, selectedPeriod: string, userName: string, onProgress?: (percentage: number) => void): Promise<number> {
    this._isImporting.next(true);
    this._importProgress.next(0);
    this._currentImportPeriod.next(selectedPeriod);
    this._isCancelled = false;

    try {
      const data = await this.readExcel(file, selectedPeriod);
      if (this._isCancelled) throw new Error('Import cancelled');

      await this.processStores(data);
      if (this._isCancelled) throw new Error('Import cancelled');

      // pos_reports is no longer used. Raw data is processed in memory.
      // await this.deleteExistingRecords(selectedPeriod);
      // if (this._isCancelled) throw new Error('Import cancelled');

      // await this.uploadToFirestore(data, (percentage) => this._importProgress.next(percentage));
      await this.saveUploadHistory(selectedPeriod, file.name, userName, data.length);
      return data.length;
    } finally {
      this._isImporting.next(false);
      this._importProgress.next(0);
      this._currentImportPeriod.next(null);
      this._isCancelled = false;
    }
  }

  async isPeriodProcessed(period: string): Promise<boolean> {
    const historyCol = collection(this.firestore, 'invoice_upload_history');
    const q = query(historyCol, where('period', '==', period), limit(1));
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  }

  async generateInvoices(period: string): Promise<void> {
    // This method is deprecated as aggregation is now done in memory during import.
    /*
    // 1. Parse period to dates
    const [yearStr, monthStr] = period.split('/');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10) - 1;
    const startDate = new Date(Date.UTC(year, month, 1));
    const endDate = new Date(Date.UTC(year, month + 1, 1));

    // 2. Fetch pos_reports
    const reportsRef = collection(this.firestore, 'pos_reports');
    const q = query(reportsRef, 
      where('transactionDate', '>=', startDate.toISOString()),
      where('transactionDate', '<', endDate.toISOString())
    );
    const snapshot = await getDocs(q);

    // 3. Aggregate
    const aggregations = new Map<string, any>();

    snapshot.forEach(doc => {
      const data = doc.data();
      const storeID = data['storeID'];
      const feeType = data['serverFeeType'];
      const amount = Number(data['SalesAmount'] || 0);

      if (!storeID) return;

      if (!aggregations.has(storeID)) {
        aggregations.set(storeID, { storeID, period, regularRevenue: 0, licensedRevenue: 0, specialRevenue: 0, idolRevenue: 0 });
      }

      const agg = aggregations.get(storeID);
      if (feeType === '일반') agg.regularRevenue += amount;
      else if (feeType === '라이센스') agg.licensedRevenue += amount;
      else if (feeType === '특수') agg.specialRevenue += amount;
      else agg.idolRevenue += amount;
    });

    // 4. Save to 'monthly_store_aggregations'
    const batchSize = 500;
    const aggArray = Array.from(aggregations.values());
    const aggCollection = collection(this.firestore, 'monthly_store_aggregations');

    for (let i = 0; i < aggArray.length; i += batchSize) {
      const batch = writeBatch(this.firestore);
      const chunk = aggArray.slice(i, i + batchSize);
      chunk.forEach(agg => batch.set(doc(aggCollection, `${period.replace('/', '-')}-${agg.storeID}`), agg));
      await batch.commit();
    }
    */
  }

  async importStoreMapping(file: File): Promise<void> {
    // 1. Read Excel data, skipping the first 5 rows
    const mappingData = await this.readStoreMappingExcel(file);

    // 2. Create any new companies and get a map of all company names to their IDs
    const companyNameToIdMap = await this.processCompaniesFromMapping(mappingData);

    // 3. Create any new stores
    await this.processStoresFromMapping(mappingData);

    // 4. Update stores with their assigned companyId
    await this.mapStoresToCompanies(mappingData, companyNameToIdMap);
  }

  private readStoreMappingExcel(file: File): Promise<any[]> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e: any) => {
            const wb = XLSX.read(e.target.result, { type: 'array' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            // The header is at row 6, so we start reading from row index 5
            const data = XLSX.utils.sheet_to_json<any>(ws, { range: 5 });
            resolve(data);
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
    });
  }

  private async processCompaniesFromMapping(data: any[]): Promise<Map<string, string>> {
    const companyNames = [...new Set(data.map(row => row['회사이름']).filter(name => name))];
    const companyMap = new Map<string, string>();
    const companiesCol = collection(this.firestore, 'company');

    const allCompaniesSnap = await getDocs(companiesCol);
    allCompaniesSnap.forEach(doc => {
        companyMap.set(doc.data()['company'], doc.id);
    });

    for (const name of companyNames) {
      if (!companyMap.has(name)) {
        const docRef = await addDoc(companiesCol, { company: name, pocName: '', email: '', phone: '', isDeleted: false, createdAt: new Date() });
        companyMap.set(name, docRef.id);
      }
    }
    return companyMap;
  }

  private async processStoresFromMapping(data: any[]): Promise<void> {
    const storeIds = [...new Set(data.map(row => row['인생네컷 그룹 ID']).filter(id => id))];
    const storesCol = collection(this.firestore, 'stores');

    const existingStores = new Map<string, { id: string, isDeleted: boolean }>();
    const allStoresSnap = await getDocs(storesCol);
    allStoresSnap.forEach(doc => { 
      const d = doc.data();
      existingStores.set(d['storeId'], { id: doc.id, isDeleted: !!d['isDeleted'] }); 
    });

    for (const storeId of storeIds) {
      const existing = existingStores.get(storeId);
      if (existing && existing.isDeleted) {
        await updateDoc(doc(this.firestore, 'stores', existing.id), { isDeleted: false });
      } else if (!existing) {
        await addDoc(storesCol, { storeId: storeId, name: storeId, location: '', isDeleted: false, createdAt: new Date() });
      }
    }
  }

  private async mapStoresToCompanies(data: any[], companyMap: Map<string, string>): Promise<void> {
    const storesCol = collection(this.firestore, 'stores');
    const storeIdToRefMap = new Map<string, any>();
    const allStoresSnap = await getDocs(storesCol);
    allStoresSnap.forEach(doc => { storeIdToRefMap.set(doc.data()['storeId'], doc.ref); });

    const storeCompanyMap = new Map<string, Set<string>>();
    for (const row of data) {
      const storeId = row['인생네컷 그룹 ID'];
      const companyId = companyMap.get(row['회사이름']);
      if (storeId && companyId) {
        if (!storeCompanyMap.has(storeId)) {
          storeCompanyMap.set(storeId, new Set());
        }
        storeCompanyMap.get(storeId)!.add(companyId);
      }
    }

    const batch = writeBatch(this.firestore);
    for (const [storeId, companyIds] of storeCompanyMap) {
      const storeRef = storeIdToRefMap.get(storeId);
      if (storeRef) {
        batch.update(storeRef, { companyIds: Array.from(companyIds) });
      }
    }
    await batch.commit();
  }

  async deleteImport(period: string): Promise<void> {
    // Delete aggregated data for the period
    const aggCollection = collection(this.firestore, 'monthly_store_aggregations');
    const q = query(aggCollection, where('period', '==', period));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const deleteBatch = writeBatch(this.firestore);
      querySnapshot.forEach((doc) => deleteBatch.delete(doc.ref));
      await deleteBatch.commit();
    }

    // Delete all history documents for the period
    const historyCol = collection(this.firestore, 'invoice_upload_history');
    const qHistory = query(historyCol, where('period', '==', period));
    const historySnapshot = await getDocs(qHistory);
    
    const historyBatch = writeBatch(this.firestore);
    historySnapshot.forEach((doc) => historyBatch.delete(doc.ref));
    await historyBatch.commit();
  }

  private readExcel(file: File, selectedPeriod: string): Promise<ReportData[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e: any) => {
        const arrayBuffer = e.target.result;
        // cellDates: true ensures dates are parsed as JS Date objects instead of serial numbers
        const wb = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
        
        // Assuming data is in the first sheet
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        
        // Convert sheet to JSON
        const rawData = XLSX.utils.sheet_to_json<any>(ws);

        // Validate missing or invalid dates
        for (let i = 0; i < rawData.length; i++) {
          const row = rawData[i];
          if (!row['기간']) {
            reject(new Error(`Row ${i + 2} is missing a date`));
            return;
          }
          const dateObj = new Date(row['기간']);
          if (isNaN(dateObj.getTime())) {
            reject(new Error(`Row ${i + 2} has an invalid date`));
            return;
          }
        }

        // Filter by date and map columns
        const filteredData: ReportData[] = rawData
          .filter(row => {
            const dateObj = new Date(row['기간']);
            if (isNaN(dateObj.getTime())) return false;

            const year = dateObj.getUTCFullYear();
            const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
            return `${year}/${month}` === selectedPeriod;
          })
          .map((row) => ({
            transactionDate: new Date(row['기간']).toISOString(), // Ensure consistent string format
            machineAccount: row['기기 계정'],
            storeID: row['지점 계정'],
            storeName: row['지점명'],
            countryType: row['국가 구분'],
            operationType: row['운영 구분'],
            serverFeeType: row['서버비 구분'],
            SalesAmount: row['매출액'],
            IP: row['IP'],
          }));

        resolve(filteredData);
      };
      
      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(file);
    });
  }

  private async commitBatch(batch: WriteBatch): Promise<void> {
    if (this._isCancelled) throw new Error('Import cancelled');

    const commitPromise = batch.commit();
    const cancelPromise = firstValueFrom(this._cancelSubject).then(() => {
      throw new Error('Import cancelled');
    });

    await Promise.race([commitPromise, cancelPromise]);
  }

  private async deleteExistingRecords(selectedPeriod: string) {
    const [yearStr, monthStr] = selectedPeriod.split('/');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10) - 1; // JS months are 0-indexed

    // Calculate start and end of the month in UTC
    const startDate = new Date(Date.UTC(year, month, 1));
    const endDate = new Date(Date.UTC(year, month + 1, 1));

    const collectionRef = collection(this.firestore, 'pos_reports');
    const q = query(collectionRef, where('transactionDate', '>=', startDate.toISOString()), where('transactionDate', '<', endDate.toISOString()));
    
    const snapshot = await getDocs(q);
    const batchSize = 500;
    const docs = snapshot.docs;

    for (let i = 0; i < docs.length; i += batchSize) {
      if (this._isCancelled) throw new Error('Import cancelled');
      const batch = writeBatch(this.firestore);
      const chunk = docs.slice(i, i + batchSize);
      chunk.forEach(doc => batch.delete(doc.ref));
      await this.commitBatch(batch);
    }
  }

  public async processStores(data: ReportData[]) {
    // Fetch countries for code lookup
    const countriesCol = collection(this.firestore, 'countries');
    const countrySnap = await getDocs(countriesCol);
    const codeToNameMap = new Map<string, string>();
    const validNamesSet = new Set<string>();

    countrySnap.forEach(doc => {
      const d = doc.data();
      if (d['code']) {
        codeToNameMap.set(String(d['code']), d['name']);
      }
      if (d['name']) {
        validNamesSet.add(d['name']);
      }
    });

    const uniqueStores = new Map<string, { name: string, countryCode: string }>();

    data.forEach(row => {
      if (row.storeID && row.storeName) {
        uniqueStores.set(row.storeID, { 
          name: row.storeName, 
          countryCode: String(row.countryType || '')
        });
      }
    });

    const storesCol = collection(this.firestore, 'stores');
    const allStoresSnap = await getDocs(storesCol);
    const existingStoresMap = new Map<string, any>();
    allStoresSnap.forEach(d => existingStoresMap.set(d.data()['storeId'], d.ref));

    let batch = writeBatch(this.firestore);
    let opCount = 0;
    const BATCH_SIZE = 500;

    for (const [storeId, info] of uniqueStores) {
      if (this._isCancelled) throw new Error('Import cancelled');
      
      let countryName = codeToNameMap.get(info.countryCode); // 1. Match by code
      if (!countryName) {
        if (validNamesSet.has(info.countryCode)) { // 2. Exact match by name
          countryName = info.countryCode;
        } else { // 3. "Contains" match by name
          for (const validName of validNamesSet) {
            if (info.countryCode.includes(validName)) {
              countryName = validName;
              break; // Found a match
            }
          }
        }
      }

      const storeRef = existingStoresMap.get(storeId);

      if (storeRef) {
        if (countryName) {
          batch.update(storeRef, { country: countryName });
          opCount++;
        }
      } else {
        const newDocRef = doc(storesCol);
        batch.set(newDocRef, {
          storeId: storeId,
          name: info.name,
          location: '',
          country: countryName || '',
          createdAt: new Date(),
          isDeleted: false
        });
        opCount++;
      }

      if (opCount >= BATCH_SIZE) {
        await this.commitBatch(batch);
        batch = writeBatch(this.firestore);
        opCount = 0;
      }
    }

    if (opCount > 0) {
      await this.commitBatch(batch);
    }
  }

  private async saveUploadHistory(period: string, fileName: string, userName: string, recordCount: number) {
    const historyCol = collection(this.firestore, 'invoice_upload_history');
    await addDoc(historyCol, {
      period,
      fileName,
      uploadedBy: userName,
      uploadedAt: new Date().toISOString(),
      recordCount
    });
  }

  private async uploadToFirestore(data: ReportData[], onProgress?: (percentage: number) => void) {
    const batchSize = 500;
    const collectionRef = collection(this.firestore, 'pos_reports');
    
    for (let i = 0; i < data.length; i += batchSize) {
      if (this._isCancelled) throw new Error('Import cancelled');
      const batch = writeBatch(this.firestore);
      const chunk = data.slice(i, i + batchSize);
      
      chunk.forEach(item => {
        const docRef = doc(collectionRef);
        batch.set(docRef, {
          ...item,
          importedAt: new Date() // Timestamp for filtering later
        });
      });
      
      await this.commitBatch(batch);

      if (onProgress && !this._isCancelled) {
        const processed = Math.min(i + batchSize, data.length);
        const percentage = Math.round((processed / data.length) * 100);
        onProgress(percentage);
      }
    }
  }
}